import { createContext, useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getQAConfig } from './utils';
import { getNeeNorDetails } from '../../../api/v2/neenor';
import { complianceQueryKeys } from '../queryHooks/useComplianceChecklist';

export const QAStoreContext = createContext(null);

const QAStoreProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const STAT_VALUE_WARNING_AMOUNT = 150000;

  const initialConfig = useMemo(() => {
    return getQAConfig();
  }, []);

  const [initialDataFetched, setInitialDataFetched] = useState(false);

  const [unifiedLoadData, setUnifiedLoadData] = useState({});
  const [consigneeData, setConsigneeData] = useState({});
  const [consignorData, setConsignorData] = useState({});
  const [invoiceCurrency, setInvoiceCurrency] = useState('');
  const [potentialLiabilityGBP, setPotentialLiabilityGBP] = useState(0);
  const [currentConfig, setCurrentConfig] = useState({ ...initialConfig });
  const [newLoadQaVat, setNewLoadQaVat] = useState({
    id: null,
    vatMessage: '',
    vatValid: true,
  });
  const [nonHybridPortModelID, setNonHybridPortModelID] = useState(0);
  const [itemTotals, setItemTotals] = useState({
    packageCount: 0,
    grossMass: 0,
    netMass: 0,
    value: 0,
    valueGbp: 0,
    dutyLiabilityGbp: 0,
    vatLiabilityGbp: 0,
    itemRows: 0,
  });
  const [statValueWarning, setStatValueWarning] = useState(false);
  const [confirmDutyRequired, setConfirmDutyRequired] = useState(false); // CABS-2707 - specifies if user must confirm dutiable load via customs MoP modal

  const [saveLoadAs, setSaveLoadAs] = useState(null);

  const fetchConsigneeData = async (consigneeID) => {
    try {
      if (consigneeID) {
        const data = await queryClient.fetchQuery({
          queryKey: complianceQueryKeys.neeNorDetails(consigneeID),
          queryFn: () => getNeeNorDetails(consigneeID),
          staleTime: 10000, // 10 seconds - to prevent multiple calls on first render
        });
        setConsigneeData(data[0] || {});
      }
    } catch (error) {
      console.error('Error fetching consignee data from getNeeNorDetails for consignee:  ', consigneeID, ' | ', error);
    }
  };

  const fetchConsignorData = async (consignorID) => {
    try {
      if (consignorID) {
        const data = await queryClient.fetchQuery({
          queryKey: complianceQueryKeys.neeNorDetails(consignorID),
          queryFn: () => getNeeNorDetails(consignorID),
          staleTime: 10000, // 10 seconds - to prevent multiple calls on first render
        });
        setConsignorData(data[0]);
      }
    } catch (error) {
      console.error('Error fetching consignor data from getNeeNorDetails for consignor:  ', consignorID, ' | ', error);
    }
  };

  useEffect(() => {
    if (itemTotals.valueGbp) {
      setStatValueWarning(itemTotals.valueGbp > STAT_VALUE_WARNING_AMOUNT);
    }
  }, [itemTotals]);

  const resetQAStore = () => {
    setInitialDataFetched(false);
    setConsigneeData({});
    setConsignorData({});
    setInvoiceCurrency('');
    setPotentialLiabilityGBP(0);
    setCurrentConfig({ ...initialConfig });
    setNewLoadQaVat({ id: null, vatMessage: '', vatValid: true });
    setNonHybridPortModelID(0);
    setItemTotals({
      packageCount: 0,
      grossMass: 0,
      netMass: 0,
      value: 0,
      valueGbp: 0,
      dutyLiabilityGbp: 0,
      vatLiabilityGbp: 0,
      itemRows: 0,
    });
    setStatValueWarning(false);
    setConfirmDutyRequired(false);
    setSaveLoadAs(null);
    setUnifiedLoadData({});
  };

  const store = useMemo(
    () => ({
      initialDataFetched: [initialDataFetched, setInitialDataFetched],
      unifiedLoadData: [unifiedLoadData, setUnifiedLoadData],
      consigneeData: [consigneeData, setConsigneeData],
      consignorData: [consignorData, setConsignorData],
      invoiceCurrency: [invoiceCurrency, setInvoiceCurrency],
      currentConfig: [currentConfig, setCurrentConfig],
      newLoadQaVat: [newLoadQaVat, setNewLoadQaVat],
      nonHybridPortModelID: [nonHybridPortModelID, setNonHybridPortModelID],
      itemTotals: [itemTotals, setItemTotals],
      potentialLiabilityGBP: [potentialLiabilityGBP, setPotentialLiabilityGBP],
      confirmDutyRequired: [confirmDutyRequired, setConfirmDutyRequired],
      statValueWarning,
      saveLoadAs: [saveLoadAs, setSaveLoadAs],
      resetQAStore,
      fetchConsigneeData,
      fetchConsignorData,
    }),
    [
      initialDataFetched,
      consigneeData,
      consignorData,
      currentConfig,
      invoiceCurrency,
      newLoadQaVat,
      nonHybridPortModelID,
      itemTotals,
      confirmDutyRequired,
      unifiedLoadData,
      resetQAStore,
      saveLoadAs,
    ]
  );

  return <QAStoreContext.Provider value={store}>{children}</QAStoreContext.Provider>;
};

export default QAStoreProvider;
